/**
 * Visit lifecycle persistence — IndexedDB schema and typed CRUD.
 *
 * Ticket #1471 Phase 1: infrastructure only. No hooks consume this yet.
 *
 * Schema extends the existing `rdm_app_offline` database (see indexedDB.ts).
 * Extending the same DB instead of creating a second one lets the future sync
 * engine run atomic cross-store transactions (write a task session AND enqueue
 * a sync action in one transaction) and reuses the generic CRUD helpers.
 *
 * Dates are stored as ISO strings to match the existing `pending_orders`
 * convention. Callers convert to Date on read.
 */

import { STORES, get, getAll, getByIndex, put, remove, clear } from './indexedDB';

/**
 * For stores with keyPath + autoIncrement, the `id` is assigned by IndexedDB
 * on insert. Callers can omit it. Reads always return a record with `id`.
 */
export type Insertable<T extends { id: number }> = Omit<T, 'id'> & { id?: number };

// ---------- Domain types ----------

export interface StoredLocation {
  lat: number;
  lng: number;
  accuracy?: number;
  timestamp: string; // ISO
}

export type DaySessionStatus = 'active' | 'ended';

export interface DaySessionRecord {
  id: number; // auto-increment
  associateId: number;
  status: DaySessionStatus;
  startTime: string; // ISO
  endTime?: string;
  startLocation?: StoredLocation;
  endLocation?: StoredLocation;
  currentLocation?: StoredLocation;
  currentActivity: 'idle' | 'driving' | 'in_store';
  currentStoreId?: number | null;
  currentProjectId?: number | null;
  totalDriveMinutes?: number;
  totalStoreMinutes?: number;
  totalMiles?: number;
  timezone?: string; // IANA tz name
}

export type TimeEntryType = 'drive' | 'store' | 'break';

export interface TimeEntryRecord {
  id: number; // auto-increment
  daySessionId: number; // FK -> day_sessions.id
  type: TimeEntryType;
  startTime: string; // ISO
  endTime?: string;
  durationMinutes?: number;
  storeId?: number;
  storeName?: string;
  projectId?: number;
  projectName?: string;
  startOdometer?: number;
  endOdometer?: number;
  startLocation?: StoredLocation;
  endLocation?: StoredLocation;
  callLogId?: number; // server-assigned
}

export type TaskSessionStatus = 'active' | 'paused' | 'completed' | 'abandoned';
export type TaskType = 'set' | 'call';

export interface TaskSessionRecord {
  uuid: string; // primary key (client-generated)
  timeEntryId?: number; // FK -> time_entries (store entry)
  taskType: TaskType;
  taskId: number;
  projectId: number;
  projectDescription: string;
  clientName?: string;
  distributorId?: number;
  // Per-project onsite workflow requirements (#2022), persisted so the active task
  // knows its required steps even offline / after a rehydrate from IndexedDB.
  requiresOrder?: boolean;
  requiresInventory?: boolean;
  requiresOos?: boolean;
  // Distributor order rules (#2022 Phase 3), persisted so the capture screen can
  // hard-enforce min/max even after an offline rehydrate (when the pending-tasks
  // fetch that normally carries them is unavailable).
  minOrderAmount?: number | null;
  maxProductCount?: number | null;
  // #3022 — deterministic team config captured from the SET assignment at task
  // start, PERSISTED so checkout still knows the team status after the set drops
  // out of the pending-tasks payload (completed sets are excluded) or after an
  // offline rehydrate/reload. Read-only source of truth for the checkout
  // indicator + submitted teamproject/teamleader.
  isTeamProject?: boolean;
  isCurrentUserLead?: boolean;
  /**
   * One TaskSessionRecord = one atomic work session = one CallLog row (#1897).
   * `startTime` is when this session began (fresh start OR resume — each is
   * a new record). `endTime` is when this session was closed (pause/finish/
   * abandon). A task paused N times produces N+1 records.
   *
   * Status reflects how THIS session ended:
   *   - active: still open
   *   - paused: rep paused — they may start a new session for this task later
   *   - completed: rep declared task done
   *   - abandoned: rep abandoned (record voided server-side if it had a callLogId)
   *
   * "Paused tasks" UI list = latest record per task whose status is `paused`.
   */
  startTime: string; // ISO — when THIS session began
  endTime?: string; // ISO — when THIS session ended
  durationMinutes?: number; // edcalltime - stcalltime, in minutes
  status: TaskSessionStatus;
  callLogId?: number | null; // server-assigned (null until synced)
}

export interface SurveyResponseEntry {
  qid: number;
  responseText: string;
}

export interface SurveyResponseRecord {
  taskUUID: string; // primary key
  surveyId?: number;
  responses: SurveyResponseEntry[];
  updatedAt: string; // ISO
  syncedAt?: string | null; // null until pushed
}

export type FileSyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';

export interface FileQueueRecord {
  id: number; // auto-increment
  taskUUID: string;
  type: 'photo' | 'document';
  blob: Blob;
  filename: string;
  mimeType: string;
  capturedAt: string; // ISO
  syncStatus: FileSyncStatus;
  syncAttempts: number;
  lastSyncAttempt?: string;
  syncError?: string;
}

export type SyncQueueStatus = 'pending' | 'syncing' | 'failed' | 'synced';
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface SyncQueueRecord {
  id: number; // auto-increment
  action: string; // e.g. 'checkin', 'start_task', 'finish_task', 'checkout', 'upload_file'
  endpoint: string;
  method: HttpMethod;
  payload: unknown;
  status: SyncQueueStatus;
  priority: number; // lower = higher priority
  retries: number;
  maxRetries: number;
  createdAt: string; // ISO
  lastAttempt?: string;
  error?: string;
  relatedTaskUUID?: string;
}

// ---------- Typed CRUD ----------

// Day sessions

export const putDaySession = (record: Insertable<DaySessionRecord>): Promise<void> =>
  put(STORES.VISIT_DAY_SESSIONS, record);

export const getDaySession = (id: number): Promise<DaySessionRecord | undefined> =>
  get<DaySessionRecord>(STORES.VISIT_DAY_SESSIONS, id);

export const getAllDaySessions = (): Promise<DaySessionRecord[]> =>
  getAll<DaySessionRecord>(STORES.VISIT_DAY_SESSIONS);

export const getActiveDaySessions = (): Promise<DaySessionRecord[]> =>
  getByIndex<DaySessionRecord>(STORES.VISIT_DAY_SESSIONS, 'status', 'active');

export const deleteDaySession = (id: number): Promise<void> =>
  remove(STORES.VISIT_DAY_SESSIONS, id);

// Time entries

export const putTimeEntry = (record: Insertable<TimeEntryRecord>): Promise<void> =>
  put(STORES.VISIT_TIME_ENTRIES, record);

export const getTimeEntry = (id: number): Promise<TimeEntryRecord | undefined> =>
  get<TimeEntryRecord>(STORES.VISIT_TIME_ENTRIES, id);

export const getTimeEntriesForDay = (daySessionId: number): Promise<TimeEntryRecord[]> =>
  getByIndex<TimeEntryRecord>(STORES.VISIT_TIME_ENTRIES, 'daySessionId', daySessionId);

export const deleteTimeEntry = (id: number): Promise<void> =>
  remove(STORES.VISIT_TIME_ENTRIES, id);

// Task sessions

export const putTaskSession = (record: TaskSessionRecord): Promise<void> =>
  put(STORES.VISIT_TASK_SESSIONS, record);

export const getTaskSession = (uuid: string): Promise<TaskSessionRecord | undefined> =>
  get<TaskSessionRecord>(STORES.VISIT_TASK_SESSIONS, uuid);

export const getAllTaskSessions = (): Promise<TaskSessionRecord[]> =>
  getAll<TaskSessionRecord>(STORES.VISIT_TASK_SESSIONS);

export const getTaskSessionsForTimeEntry = (timeEntryId: number): Promise<TaskSessionRecord[]> =>
  getByIndex<TaskSessionRecord>(STORES.VISIT_TASK_SESSIONS, 'timeEntryId', timeEntryId);

export const getTaskSessionsByStatus = (status: TaskSessionStatus): Promise<TaskSessionRecord[]> =>
  getByIndex<TaskSessionRecord>(STORES.VISIT_TASK_SESSIONS, 'status', status);

export const deleteTaskSession = (uuid: string): Promise<void> =>
  remove(STORES.VISIT_TASK_SESSIONS, uuid);

// Survey responses (keyed by taskUUID)

export const putSurveyResponse = (record: SurveyResponseRecord): Promise<void> =>
  put(STORES.VISIT_SURVEY_RESPONSES, record);

export const getSurveyResponse = (taskUUID: string): Promise<SurveyResponseRecord | undefined> =>
  get<SurveyResponseRecord>(STORES.VISIT_SURVEY_RESPONSES, taskUUID);

export const deleteSurveyResponse = (taskUUID: string): Promise<void> =>
  remove(STORES.VISIT_SURVEY_RESPONSES, taskUUID);

// File queue

export const putFileQueueEntry = (record: Insertable<FileQueueRecord>): Promise<void> =>
  put(STORES.VISIT_FILE_QUEUE, record);

export const getFileQueueEntry = (id: number): Promise<FileQueueRecord | undefined> =>
  get<FileQueueRecord>(STORES.VISIT_FILE_QUEUE, id);

export const getFileQueueForTask = (taskUUID: string): Promise<FileQueueRecord[]> =>
  getByIndex<FileQueueRecord>(STORES.VISIT_FILE_QUEUE, 'taskUUID', taskUUID);

export const getPendingFiles = (): Promise<FileQueueRecord[]> =>
  getByIndex<FileQueueRecord>(STORES.VISIT_FILE_QUEUE, 'syncStatus', 'pending');

export const deleteFileQueueEntry = (id: number): Promise<void> =>
  remove(STORES.VISIT_FILE_QUEUE, id);

// Sync queue

export const putSyncQueueEntry = (record: Insertable<SyncQueueRecord>): Promise<void> =>
  put(STORES.VISIT_SYNC_QUEUE, record);

export const getSyncQueueEntry = (id: number): Promise<SyncQueueRecord | undefined> =>
  get<SyncQueueRecord>(STORES.VISIT_SYNC_QUEUE, id);

export const getPendingSyncEntries = (): Promise<SyncQueueRecord[]> =>
  getByIndex<SyncQueueRecord>(STORES.VISIT_SYNC_QUEUE, 'status', 'pending');

export const getAllSyncEntries = (): Promise<SyncQueueRecord[]> =>
  getAll<SyncQueueRecord>(STORES.VISIT_SYNC_QUEUE);

export const deleteSyncQueueEntry = (id: number): Promise<void> =>
  remove(STORES.VISIT_SYNC_QUEUE, id);

// Singleton store — small key/value for visit-level transient state
// (current store visit metadata, drive confirmation, GPS).

export interface SingletonRecord<T = unknown> {
  key: string;
  value: T;
  updatedAt: string;
}

export const putSingleton = <T>(key: string, value: T): Promise<void> =>
  put<SingletonRecord<T>>(STORES.VISIT_SINGLETONS, {
    key,
    value,
    updatedAt: new Date().toISOString(),
  });

export const getSingleton = async <T>(key: string): Promise<T | undefined> => {
  const row = await get<SingletonRecord<T>>(STORES.VISIT_SINGLETONS, key);
  return row?.value;
};

export const deleteSingleton = (key: string): Promise<void> =>
  remove(STORES.VISIT_SINGLETONS, key);

// Bulk cleanup (used by startDay)

export const clearAllVisitData = async (): Promise<void> => {
  await clear(STORES.VISIT_DAY_SESSIONS);
  await clear(STORES.VISIT_TIME_ENTRIES);
  await clear(STORES.VISIT_TASK_SESSIONS);
  await clear(STORES.VISIT_SURVEY_RESPONSES);
  await clear(STORES.VISIT_FILE_QUEUE);
  await clear(STORES.VISIT_SYNC_QUEUE);
  await clear(STORES.VISIT_SINGLETONS);
};

// UUID generator for task sessions — RFC 4122 v4 format

export const generateTaskUUID = (): string =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

// Local-first read helpers (#1983/#1986 Phase 2). The renderer reads
// these IDB-backed visits first, then hydrates from the server in the
// background — so a network drop can never produce "Unable to load call"
// (diag #33). Returned as loose records because the dual-write ops
// (Phase 1) write a wider shape into visit_task_sessions than the
// useVisitTask TaskSessionRecord; these readers stay shape-agnostic.

export const getVisitByLocalId = async (
  localId: string,
): Promise<Record<string, unknown> | null> => {
  const all = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
  const match = all.find((r) => r.uuid === localId);
  // A row flagged pendingDelete (#1987) is being torn down by deleteTimeEntry
  // — it lingers only so sync() can resolve the calllogid; hide it from reads.
  return match && !match.pendingDelete ? match : null;
};

export const getVisitsForDate = async (
  dateYYYYMMDD: string,
): Promise<Array<Record<string, unknown>>> => {
  const all = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
  return all.filter((r) => {
    if (r.pendingDelete) return false; // hide rows awaiting a queued DELETE (#1987)
    const st = r.startTime;
    return typeof st === 'string' && st.startsWith(dateYYYYMMDD);
  });
};

export const getActiveVisits = async (): Promise<Array<Record<string, unknown>>> => {
  const all = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
  return all.filter((r) => r.status === 'in_progress' && !r.pendingDelete);
};
